import { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import TemplateFormDialog from "@/components/dialogs/TemplateFormDialog";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ListChecks, Zap } from "lucide-react";

export default function Templates() {
  const [templates, setTemplates] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);

  const load = () => api.get("/templates").then((r) => setTemplates(r.data)).catch(() => {});

  useEffect(() => {
    load();
  }, []);

  const deleteTemplate = async (id) => {
    try {
      await api.delete(`/templates/${id}`);
      toast.success("Template deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <DashboardLayout title="Templates & Checklist Engine">
      <div className="flex items-center justify-between mb-5">
        <p className="text-sm text-[#4B5563]">Reusable service checklists (ESG, GHG, or custom) that can be assigned to any client from their detail panel.</p>
        <Button data-testid="add-template-button" onClick={() => { setEditingTemplate(null); setFormOpen(true); }} className="bg-[#76C121] hover:bg-[#5C9819] text-white shrink-0 ml-4">
          <Plus className="h-4 w-4 mr-1.5" /> New Template
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="templates-grid">
        {templates.length === 0 && <p className="text-sm text-[#4B5563] col-span-3 text-center py-10">No templates yet. Create your first checklist template.</p>}
        {templates.map((t) => (
          <div key={t.id} data-testid={`template-card-${t.id}`} className="bg-white border border-[#E2E8F0] rounded-lg p-4 flex flex-col">
            <div className="flex items-start justify-between mb-2">
              <span className="text-xs font-medium uppercase tracking-wide text-[#00823B] bg-[#76C121]/10 px-2 py-0.5 rounded-md">{t.category}</span>
              {t.auto_create_tasks && (
                <span className="text-xs text-[#4B5563] flex items-center gap-1"><Zap className="h-3 w-3" /> Auto-tasks</span>
              )}
            </div>
            <p className="text-sm font-semibold text-[#1C1C1E]">{t.name}</p>
            <p className="text-xs text-[#4B5563] mt-1 line-clamp-2">{t.description}</p>
            <p className="text-xs text-[#4B5563] mt-2 flex items-center gap-1"><ListChecks className="h-3.5 w-3.5" /> {t.items.length} items</p>
            <div className="flex gap-2 mt-3 pt-3 border-t border-[#E2E8F0]">
              <Button size="sm" variant="outline" data-testid={`edit-template-${t.id}`} onClick={() => { setEditingTemplate(t); setFormOpen(true); }} className="flex-1">
                <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button data-testid={`delete-template-${t.id}`} className="h-8 w-8 flex items-center justify-center rounded-md border border-[#E2E8F0] text-red-600 hover:bg-red-50">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </AlertDialogTrigger>
                <AlertDialogContent data-testid={`delete-template-confirm-${t.id}`}>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete "{t.name}"?</AlertDialogTitle>
                    <AlertDialogDescription>Existing client checklists already assigned from this template will not be affected. This action cannot be undone.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={() => deleteTemplate(t.id)} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        ))}
      </div>

      <TemplateFormDialog open={formOpen} onOpenChange={setFormOpen} template={editingTemplate} onSaved={load} />
    </DashboardLayout>
  );
}
